import { Mail, Clock, Shield } from 'lucide-react';
import SEO from '../components/SEO';
import LocaleLink from '../components/LocaleLink';
import { useLanguage } from '../contexts/LanguageContext';

const DataDeletionPage = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  return (
  <div className="pt-16">
    <SEO
      titleEn="Data Deletion Instructions | Quidory"
      titleAr="تعليمات حذف البيانات | كويدوري"
      descEn="Learn how to request deletion of your personal data from Quidory. Step-by-step instructions for account and data removal."
      descAr="تعرف على كيفية طلب حذف بياناتك الشخصية من كويدوري. خطوات إزالة حسابك وبياناتك."
      path="/data-deletion"
    />
    <section className="py-20 lg:py-24 bg-background">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-gold/10">
              <Shield className="h-5 w-5 text-gold" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gold">
              {isAr ? 'حقوق البيانات' : 'Data Rights'}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-foreground mb-2">
            {isAr ? 'تعليمات حذف البيانات' : 'Data Deletion Instructions'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isAr ? 'آخر تحديث: ١ يناير ٢٠٢٥' : 'Last updated: January 1, 2025'}
          </p>
        </div>

        <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">

          <div>
            <h2 className="text-base font-semibold text-foreground mb-3">Your Right to Data Deletion</h2>
            <p>Quidory respects your right to control your personal data. You have the right to request the deletion of your personal data that we have collected and stored. This page explains how to exercise this right.</p>
            <p className="mt-3">We comply with applicable data protection regulations including GDPR (for European users) and other regional privacy laws that grant users the right to erasure.</p>
          </div>

          {/* Methods */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-5 rounded-xl border border-border bg-card">
              <div className="flex items-center gap-2.5 mb-3">
                <Mail className="h-4 w-4 text-gold" />
                <h3 className="text-sm font-semibold text-foreground">Method 1: Email Request</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Send a deletion request email to our data team:</p>
              <a href="mailto:support@quidory.com?subject=Data Deletion Request" className="text-xs font-medium text-gold hover:text-gold/80 transition-colors">
                support@quidory.com
              </a>
              <p className="text-xs text-muted-foreground mt-3">Subject: <strong className="text-foreground">Data Deletion Request</strong></p>
              <p className="text-xs text-muted-foreground mt-1">Include your registered email address and account details.</p>
            </div>

            <div className="p-5 rounded-xl border border-border bg-card">
              <div className="flex items-center gap-2.5 mb-3">
                <Shield className="h-4 w-4 text-gold" />
                <h3 className="text-sm font-semibold text-foreground">Method 2: Account Settings</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3">If you have an active account:</p>
              <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal ps-4">
                <li>Log in to your Quidory account</li>
                <li>Go to Settings → Account</li>
                <li>Scroll to "Data & Privacy"</li>
                <li>Click "Delete My Account"</li>
                <li>Confirm the deletion</li>
              </ol>
            </div>
          </div>

          <div>
            <h2 className="text-base font-semibold text-foreground mb-3">What Data Will Be Deleted</h2>
            <p>Upon successful verification, we will delete:</p>
            <ul className="list-disc ps-5 space-y-1.5 mt-2">
              <li>Your account information (name, email, profile data)</li>
              <li>All content you have created or published through the platform</li>
              <li>Your preferences and settings</li>
              <li>Usage history and analytics data associated with your account</li>
              <li>API keys and integration configurations</li>
              <li>Billing information (subject to legal retention requirements)</li>
            </ul>
          </div>

          <div>
            <h2 className="text-base font-semibold text-foreground mb-3">Data We May Retain</h2>
            <p>In certain circumstances, we may be required to retain some data even after a deletion request:</p>
            <ul className="list-disc ps-5 space-y-1.5 mt-2">
              <li><strong className="text-foreground">Legal Obligations:</strong> Transaction records for tax and accounting purposes (typically 7 years)</li>
              <li><strong className="text-foreground">Fraud Prevention:</strong> Information necessary to prevent fraud or abuse</li>
              <li><strong className="text-foreground">Legal Disputes:</strong> Data relevant to ongoing legal proceedings</li>
              <li><strong className="text-foreground">Anonymized Data:</strong> Aggregated, anonymized analytics data that cannot identify you</li>
            </ul>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4 text-gold" />
              <h2 className="text-base font-semibold text-foreground">Processing Timeline</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { step: '1', label: 'Verification', desc: 'We verify your identity (1–2 business days)' },
                { step: '2', label: 'Processing', desc: 'Deletion is processed across all systems (up to 30 days)' },
                { step: '3', label: 'Confirmation', desc: 'You receive a confirmation email' },
              ].map((s) => (
                <div key={s.step} className="p-4 rounded-lg border border-border bg-card">
                  <div className="h-6 w-6 rounded-full bg-gold/15 flex items-center justify-center mb-2">
                    <span className="text-xs font-bold text-gold">{s.step}</span>
                  </div>
                  <p className="text-xs font-semibold text-foreground mb-1">{s.label}</p>
                  <p className="text-xs text-muted-foreground">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="p-5 rounded-xl border border-gold/20 bg-gold/5">
            <h2 className="text-sm font-semibold text-foreground mb-2">Facebook / Meta App Data Deletion</h2>
            <p className="text-xs text-muted-foreground">
              If you connected Quidory through a Facebook or Meta application, you can also request data deletion through Meta's platform. Go to your Facebook Settings → Apps and Websites, find Quidory, and select "Remove." Additionally, send an email to <strong className="text-foreground">support@quidory.com</strong> with the subject "Meta App Data Deletion Request" to ensure complete data removal from our systems.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-foreground mb-3">Contact for Data Requests</h2>
            <p>For any data-related requests or questions, contact our Data Protection team:</p>
            <p className="mt-2">
              <strong className="text-foreground">Email:</strong>{' '}
              <a href="mailto:support@quidory.com" className="text-gold hover:text-gold/80 transition-colors">
                support@quidory.com
              </a>
            </p>
            <p className="mt-1"><strong className="text-foreground">Legal Entity:</strong> Al Yakhour LLC</p>
            <p className="mt-1"><strong className="text-foreground">Registered Office:</strong> Wyoming, USA</p>
            <p className="mt-1"><strong className="text-foreground">Website:</strong> https://quidory.com</p>
            <p className="mt-1"><strong className="text-foreground">Response Time:</strong> Within 30 days of verified request</p>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-border">
          <LocaleLink to="/privacy" className="text-sm text-gold hover:text-gold/80 transition-colors">
            {isAr ? '← عرض سياسة الخصوصية الكاملة' : '← View Full Privacy Policy'}
          </LocaleLink>
        </div>
      </div>
    </section>
  </div>
  );
};

export default DataDeletionPage;
